import { apiClient } from '@/http/api-client';
import {
  adminRolesResponseSchema,
  adminUsersResponseSchema,
  adminMutationResponseSchema,
} from './users-schemas';
import { z } from 'zod';

export interface AdminRole {
  id: string;
  name: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  roles: AdminRole[];
  createdAt: string;
}

export type AdminStatus = AdminUser['status'];

export interface ListAdminsResult {
  users: AdminUser[];
}

export interface CreateAdminRequest {
  email: string;
  name: string;
  password: string;
  roleIds: string[];
}

export interface UpdateAdminRequest {
  id: string;
  email: string;
  name: string;
  roleIds?: string[];
}

const deleteResponseSchema = z.object({ data: z.object({ id: z.uuid() }) });

export const usersApi = {
  listAdmins: async (): Promise<ListAdminsResult> => {
    const response = await apiClient.get('/v1/im/admin/users', adminUsersResponseSchema);
    return {
      users: response.data.map((user) => ({
        id: user.id,
        email: user.email,
        name: user.full_name,
        status: user.active ? 'ACTIVE' : 'INACTIVE',
        roles: user.roles,
        createdAt: user.created_at,
      })),
    };
  },

  listRoles: async (): Promise<AdminRole[]> => {
    const response = await apiClient.get('/v1/im/admin/roles', adminRolesResponseSchema);
    return response.data;
  },

  createAdmin: async (request: CreateAdminRequest): Promise<void> => {
    await apiClient.post(
      '/v1/im/admin',
      {
        full_name: request.name,
        email: request.email,
        password: request.password,
        roles: request.roleIds,
      },
      adminMutationResponseSchema
    );
  },

  updateAdmin: async (request: UpdateAdminRequest): Promise<void> => {
    let roleIds = request.roleIds;
    if (roleIds === undefined) {
      const directory = await usersApi.listAdmins();
      const current = directory.users.find((user) => user.id === request.id);
      if (!current) throw new Error('Admin account was not found');
      roleIds = current.roles.map((role) => role.id);
    }
    if (roleIds.length === 0) throw new Error('An admin role is required');
    await apiClient.put(
      '/v1/im/admin',
      { full_name: request.name, email: request.email, roles: roleIds },
      adminMutationResponseSchema
    );
  },

  deleteAdmin: async (adminId: string): Promise<void> => {
    await apiClient.delete(`/v1/im/admin/${encodeURIComponent(adminId)}`, deleteResponseSchema);
  },
};
